import React, { useState, useEffect } from 'react';
import Grid from '@mui/material/Grid';
import Card from '@mui/material/Card';
import Icon from '@mui/material/Icon';
import Button from '@mui/material/Button';
import { useNavigate, useLocation } from 'react-router-dom';
import MDBox from 'components/MDBox';
import MDButton from 'components/MDButton';
import MDTypography from 'components/MDTypography';
import DataTable from 'examples/Tables/DataTable';
import DashboardLayout from 'examples/LayoutContainers/DashboardLayout';

import Footer from 'examples/Footer';
import MDPagination from 'components/MDPagination';
import { apiGetUsers, apiExcelUser, apiDeleteUser } from '../../api/user.api';
import confirmHardDelete from 'utils/confirmHardDelete';
import { useData } from '../../hooks';
import TopNavbar from 'examples/Navbars/TopNavbar';

const Members = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchcond, setSearchcond] = useState('');
  const navigate = useNavigate();
  const location = useLocation();
  const { user, appIsReady } = useData();

  // URL에서 페이지와 검색 조건 정보 추출
  const searchParams = new URLSearchParams(location.search);
  const initialPage = parseInt(searchParams.get('page')) || 1;
  const initialSearchCond = searchParams.get('searchcond') || '';

  const [page, setPage] = useState(initialPage);
  const [total, setTotal] = useState(0);

  useEffect(() => {
    // 검색 조건을 상태에 설정
    if (appIsReady && user) {
      setSearchcond(initialSearchCond);
      fetchUsers(page, initialSearchCond);  // 검색 조건과 페이지를 함께 전달
    }
  }, [page, user, appIsReady]);

  const fetchUsers = async (pageNum, search) => {
    setLoading(true);
    try {
      console.log("searchcond=" + search);
      const response = await apiGetUsers(pageNum, 10, user.accessToken, search); // 페이지 당 10명의 사용자 데이터 불러오기
      if (response && response.data) {
        setUsers(response.data);
        setTotal(response.total); // 전체 데이터 수 설정
      } else {
        console.log('No data received');
      }
    } catch (error) {
      console.error('Failed to load users:', error);
    } finally {
      setLoading(false);
    }
  };

  const handlePageChange = (pageNum) => {
    setPage(pageNum);
    const newSearchParams = new URLSearchParams(location.search);
    newSearchParams.set('page', pageNum);
    newSearchParams.set('searchcond', searchcond); // 검색 조건도 함께 설정
    navigate({ search: newSearchParams.toString() });
    fetchUsers(pageNum, searchcond);  // 페이지 변경 시에도 검색 조건을 함께 전달
  };

  const handleRowClick = (userdata) => {
    navigate(`/memberdetail`, { state: { userdata, page, searchcond } });
  };

  const handleSearchChange = (event) => {
    setSearchcond(event.target.value);
  };

  const handleSearchSubmit = (event) => {
    event.preventDefault();
    setPage(1);
    const newSearchParams = new URLSearchParams(location.search);
    newSearchParams.set('page', 1);
    newSearchParams.set('searchcond', searchcond);
    navigate({ search: newSearchParams.toString() });
    fetchUsers(1, searchcond);  // 검색 조건이 변경되면 첫 페이지부터 다시 검색
  };

  const formatDate = (dateString) => {
    const options = { year: 'numeric', month: '2-digit', day: '2-digit' };
    return new Date(dateString).toLocaleString('ko-KR', options).replace(/. /g, '-').replace('.', '').replace(' ', ' ').slice(0, 10);
  };

  /**
   * 회원 삭제. 🔴 실회원의 포인트와 적립 이력이 걸려 있다. 가장 위험한 삭제다.
   *    서버는 softDelete 라 `deletedAt` 을 비우면 되살아나지만,
   *    ⚠️ **이름·이메일·전화번호는 DB 에 그대로 남는다** — 개인정보가 지워지는 것이 아니다.
   *
   * 🔴 이 핸들러는 **map 바깥**에 있어야 한다. 아래 `users.map(user => …)` 의 파라미터가
   *    로그인한 관리자 `user` 를 가리기 때문에, 안에서 쓰면 관리자 토큰이 아니라
   *    **그 행의 회원 객체**를 참조하게 된다.
   */
  const handleDeleteClick = async (member) => {
    if (!confirmHardDelete(`회원 '${member.name}' (${member.userid})`)) return;
    try {
      await apiDeleteUser(member.id, user.accessToken);
      fetchUsers(page, searchcond);
    } catch (e) {
      alert('삭제에 실패했습니다. 잠시 후 다시 시도해 주세요.');
    }
  };

  const rows = users.map(user => ({
    original: user,
    avatar: <Icon fontSize='10'>account_circle</Icon>,
    name: user.name,
    userid: user.userid,
    email: user.email,
    phone: user.phone,
    rpoint: user.rpoint,
    oldRpoint: user.oldRpoint,
    mpoint: user.mpoint,
    spoint: user.spoint,
    receivable: user.receivable === 1 ? '가능' : '불가',
    giftPurchase: user.isGiftPurchaseLocked === true ? '차단' : '가능',
    userType: user.userType,
    createdAt: formatDate(user.createdAt),
    remove:
      <Button
        variant="outlined"
        color="error"
        size="small"
        onClick={(e) => { e.stopPropagation(); handleDeleteClick(user); }}
      >
        삭제
      </Button>,
  }));

  const columns = [
    { Header: "", accessor: "avatar", width: '1%', align: "left" },
    { Header: "이름", accessor: "name", align: "left" },
    { Header: "userid", accessor: "userid", align: "left" },
    { Header: "이메일", accessor: "email", align: "left" },
    { Header: "전화번호", accessor: "phone", align: "center" },
    { Header: "rpoint", accessor: "rpoint", align: "center" },
    { Header: "mpoint", accessor: "mpoint", align: "center" },
    { Header: "구rpoint", accessor: "oldRpoint", align: "center" },
    { Header: "spoint", accessor: "spoint", align: "center" },
    { Header: "P수령가능", accessor: "receivable", align: "center" },
    { Header: "기프티콘구매", accessor: "giftPurchase", align: "center" },
    { Header: "구분", accessor: "userType", align: "left" },
    { Header: "가입일", accessor: "createdAt", align: "left" },
    { Header: "삭제", accessor: "remove", align: "center" },
  ];

  const handleDownloadExcel = async () => {
    try {
    
      const currentSearchCond = searchcond || "";
        // 엑셀 파일 데이터 가져오기
      const response = await apiExcelUser(user.accessToken, currentSearchCond);
      console.log(response);
         // JSON 응답을 텍스트로 읽기
    const jsonText = await response.text();
    const jsonData = JSON.parse(jsonText);
    
    // 버퍼 데이터 추출
    const bufferData = jsonData.data.data;
    
    // Uint8Array로 변환
    const uint8Array = new Uint8Array(bufferData);
    
    // 올바른 MIME 타입으로 Blob 생성
    const excelBlob = new Blob([uint8Array], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
    });
    
    // 다운로드 링크 생성
    const url = window.URL.createObjectURL(excelBlob);
    const a = document.createElement('a');
    a.href = url;
    a.download = '회원.xlsx';
    document.body.appendChild(a);
    a.click();
    
    // 정리
    window.URL.revokeObjectURL(url);
    document.body.removeChild(a);
      } catch (error) {
        console.error('다운로드 실패:', error);
      }
};

const handleAddMember = () => {
  navigate('/member/add');
};

  // 페이지 번호 계산
  const totalPages = Math.ceil(total / 10);
  const maxPageNumbers = 10;
  const currentPageGroup = Math.floor((page - 1) / maxPageNumbers);
  const startPage = currentPageGroup * maxPageNumbers + 1;
  const endPage = Math.min(startPage + maxPageNumbers - 1, totalPages);
  const pageNumbers = Array.from({ length: endPage - startPage + 1 }, (_, index) => startPage + index);

  return (
    <DashboardLayout>
      <TopNavbar
        searchlabel="이름,아이디,이메일,전화번호"
        onSearchChange={handleSearchChange}
        onSearchSubmit={handleSearchSubmit}
      />
      <MDBox display="flex" mr={2} justifyContent="right">
      <MDButton variant="gradient" color="info" onClick={handleAddMember} sx={{ ml: 2 }}>회원 추가하기</MDButton>
        <MDButton variant="gradient" color="info" onClick={handleDownloadExcel} sx={{ ml: 2 }}>엑셀 다운로드</MDButton>
      </MDBox>
      <MDBox pt={6} pb={3}>
        <Grid container spacing={6}>
          <Grid item xs={12}>
            <Card>
              <MDBox
                mx={2}
                mt={-3}
                py={3}
                px={2}
                variant="gradient"
                bgColor="info"
                borderRadius="lg"
                coloredShadow="info"
              >
                <MDTypography variant="h6" color="white">
                  회원관리
                </MDTypography>
              </MDBox>
              <MDBox pt={3}>
                <DataTable
                  table={{ columns, rows }}
                  isSorted={false}
                  entriesPerPage={false}
                  showTotalEntries={false}
                  noEndBorder
                  loading={loading}
                  onRowClick={(row) => handleRowClick(row.original)}
                />
              </MDBox>
              <MDBox
                display="flex"
                flexDirection={{ xs: "column", sm: "row" }}
                justifyContent="center"
                alignItems={{ xs: "flex-start", sm: "center" }}
                p={3}
              >
                <MDPagination
                  variant="gradient"
                  color="info"
                >
                  {page > 1 && (
                    <MDPagination item onClick={() => handlePageChange(page - 1)}>
                      <Icon sx={{ fontWeight: "bold" }}>chevron_left</Icon>
                    </MDPagination>
                  )}
                  {pageNumbers.map(pageNum => (
                    <MDPagination
                      item
                      key={pageNum}
                      onClick={() => handlePageChange(pageNum)}
                      active={page === pageNum}
                    >
                      {pageNum}
                    </MDPagination>
                  ))}
                  {page < totalPages && (
                    <MDPagination item onClick={() => handlePageChange(page + 1)}>
                      <Icon sx={{ fontWeight: "bold" }}>chevron_right</Icon>
                    </MDPagination>
                  )}
                </MDPagination>
              </MDBox>
            </Card>
          </Grid>
        </Grid>
      </MDBox>
      <Footer />
    </DashboardLayout>
  );
}

export default Members;
